<template>
    <button
        :disabled="isDisabled"
        :id="id"
        class="base-button rounded-full transition-all relative"
        :class="[
            button({ color, variant }),
            `size-${size}`,
            `variant-${variant}`,
            square ? 'square' : '',
            appendIcon ? 'has-append' : '',
            prependIcon ? 'has-prepend' : '',
            state == 'loading' ? 'loading' : '',
            block ? 'w-full justify-center' : '',
            $attrs.customGradient,
        ]"
    >
        <Icon v-if="state == 'loading'" name="svg-spinners:bars-rotate-fade" />
        <div :id="id" class="base-button-inner flex items-center" v-else>
            <div :id="id" class="base-button-prepend mr-2 flex justify-center items-center" v-if="prependIcon">
                <Icon :name="prependIcon" />
            </div>
            <div class="absolute -top-3 -right-0">
                <slot name="badge" />
            </div>

            <slot />

            <div :id="id" class="base-button-append ml-2 flex justify-center items-center" v-if="appendIcon">
                <Icon :name="appendIcon" />
            </div>
        </div>
    </button>
</template>

<script setup lang="ts">
// * cva
import { cva, type VariantProps } from "class-variance-authority";

// * interfaces
interface Props {
    block?: boolean;
    prependIcon?: string;
    appendIcon?: string;
    square?: boolean;
    state?: "default" | "loading" | "disabled";
    size?: "xsm" | "sm" | "md" | "lg";
    variant?: "primary" | "outlined" | "ghost";
    color?: "brand" | "value" | "aqua" | "gray" | "yellow" | "green" | "red" | "blue" | "white";
    id?: string;
}

// * props
const props = withDefaults(defineProps<Props>(), {
    size: "md",
    square: false,
    color: "brand",
    state: "default",
    variant: "primary",
});

const button = cva("", {
    variants: {
        color: {
            brand: "brand",
            value: "value",
            aqua: "aqua",
            gray: "gray",
            yellow: "yellow",
            green: "green",
            red: "red",
            blue: "blue",
            white: "white",
        },
        variant: {
            primary: "primary",
            outlined: "outlined",
            ghost: "ghost",
        },
    },
    compoundVariants: [
        {
            variant: "primary",
            color: "brand",
            class: "bg-brand-500 text-gray-100 hover:bg-brand-400 disabled:bg-gray-800 disabled:text-gray-700  disabled:border-gray-700",
        },
        {
            variant: "primary",
            color: "value",
            class: "bg-value-500 text-gray-100 hover:bg-value-400  disabled:bg-gray-800 disabled:text-gray-700 disabled:border  disabled:border-gray-700",
        },
        {
            variant: "primary",
            color: "aqua",
            class: "bg-aqua-500 text-gray-100 hover:bg-aqua-400 disabled:bg-tra disabled:text-gray-700 disabled:border  disabled:border-gray-700",
        },
        {
            variant: "primary",
            color: "gray",
            class: "bg-gray-500 text-gray-100 hover:bg-gray-400  disabled:bg-gray-800 disabled:text-gray-700  disabled:border disabled:border-gray-700",
        },
        {
            variant: "primary",
            color: "yellow",
            class: "bg-yellow-400 text-gray-100 hover:bg-yellow-200  disabled:bg-gray-800 disabled:text-gray-700 disabled:border  disabled:border-gray-700",
        },
        {
            variant: "primary",
            color: "green",
            class: "bg-green-400 text-gray-100 hover:bg-green-200  disabled:bg-gray-800 disabled:text-gray-700  disabled:border disabled:border-gray-700",
        },
        {
            variant: "primary",
            color: "red",
            class: "bg-red-800 text-gray-100 hover:bg-red-400 disabled:bg-gray-800 disabled:text-gray-700  disabled:border disabled:border-gray-700",
        },
        {
            variant: "primary",
            color: "blue",
            class: "bg-blue-400 text-gray-100 hover:bg-blue-200 disabled:bg-gray-800 disabled:text-gray-700 disabled:border  disabled:border-gray-700",
        },

        {
            variant: "ghost",
            color: "brand",
            class: "bg-transparent text-brand-300 hover:text-brand-100 hover:bg-transparent  disabled:bg-transparent  disabled:border disabled:text-gray-700",
        },
        {
            variant: "ghost",
            color: "white",
            class: "bg-transparent text-gray-100 hover:text-gray-300 hover:bg-transparent  disabled:bg-transparent disabled:text-gray-700",
        },
        {
            variant: "ghost",
            color: "value",
            class: "bg-transparent text-value-300 hover:text-value-100 hover:bg-transparent  disabled:bg-transparent disabled:text-gray-700",
        },
        {
            variant: "ghost",
            color: "aqua",
            class: "bg-transparent text-aqua-300 hover:text-aqua-100 hover:bg-transparent disabled:bg-transparent disabled:text-gray-700",
        },
        {
            variant: "ghost",
            color: "gray",
            class: "bg-transparent text-gray-300 hover:text-gray-100 hover:bg-transparent disabled:bg-transparent disabled:text-gray-700",
        },
        {
            variant: "ghost",
            color: "yellow",
            class: "bg-transparent text-yellow-200 hover:text-yellow-100 hover:bg-transparent  disabled:bg-transparent disabled:text-gray-700",
        },
        {
            variant: "ghost",
            color: "green",
            class: "bg-transparent text-green-200 hover:text-green-100 hover:bg-transparent  disabled:bg-transparent disabled:text-gray-700",
        },
        {
            variant: "ghost",
            color: "red",
            class: "bg-transparent text-red-200 hover:text-red-100 hover:bg-transparent  disabled:bg-transparent disabled:text-gray-700",
        },
        {
            variant: "ghost",
            color: "blue",
            class: "bg-transparent text-blue-200 hover:text-blue-100 hover:bg-transparent  disabled:bg-transparent disabled:text-gray-700",
        },

        {
            variant: "outlined",
            color: "brand",
            class: "shadow-border-1 shadow-brand-300 text-brand-300 hover:shadow-brand-100 hover:bg-transparent hover:text-brand-100  disabled:text-gray-800 disabled:shadow-gray-700 disabled:shadow-border-1",
        },
        {
            variant: "outlined",
            color: "value",
            class: "shadow-border-1 shadow-value-500 text-value-500 hover:shadow-value-100 hover:bg-transparent hover:text-value-100  disabled:text-gray-800 disabled:shadow-gray-700 disabled:shadow-border-1",
        },
        {
            variant: "outlined",
            color: "aqua",
            class: "shadow-border-1 shadow-aqua-500 text-aqua-500 hover:shadow-aqua-100 hover:bg-transparent hover:text-aqua-100  disabled:text-gray-800 disabled:shadow-gray-700 disabled:shadow-border-1",
        },
        {
            variant: "outlined",
            color: "gray",
            class: "shadow-border-1 shadow-gray-500 text-gray-500 hover:shadow-gray-100 hover:bg-transparent hover:text-gray-100  disabled:text-gray-800 disabled:shadow-gray-700 disabled:shadow-border-1",
        },
        {
            variant: "outlined",
            color: "yellow",
            class: "shadow-border-1 shadow-yellow-400 text-yellow-400 hover:shadow-yellow-100 hover:bg-transparent hover:text-yellow-100  disabled:text-yellow-800 disabled:shadow-yellow-800 disabled:shadow-border-1",
        },
        {
            variant: "outlined",
            color: "green",
            class: "shadow-border-1 shadow-green-400 text-green-400 hover:shadow-green-100 hover:bg-transparent hover:text-green-100  disabled:text-green-800 disabled:shadow-green-800 disabled:shadow-border-1",
        },
        {
            variant: "outlined",
            color: "red",
            class: "shadow-border-1 shadow-red-400 text-red-400 hover:shadow-red-100 hover:bg-transparent hover:text-red-100  disabled:text-red-800 disabled:shadow-red-800 disabled:shadow-border-1",
        },
        {
            variant: "outlined",
            color: "blue",
            class: "shadow-border-1 shadow-blue-400 text-blue-400 hover:shadow-blue-100 hover:bg-transparent hover:text-blue-100  disabled:text-blue-800 disabled:shadow-blue-800  disabled:shadow-border-1",
        },
        {
            variant: "primary",
            color: "white",
            class: "bg-gray-100 text-brand-500 hover:bg-gray-300",
        },
        {
            variant: "outlined",
            color: "white",
            class: "shadow-border-1 shadow-gray-100 text-gray-100 hover:shadow-gray-100 hover:bg-transparent hover:text-gray-100  disabled:text-gray-100 disabled:shadow-gray-100  disabled:shadow-border-1",
        },
    ],
    defaultVariants: {
        color: "brand",
        variant: "primary",
    },
});

// * computed properties
const isDisabled = computed(() => {
    return ["disabled", "loading"].includes(props.state);
});
</script>

<style lang="scss">
@import "../../assets/scss/app-button/app-button.scss";
</style>
